(function(){
'use strict';

/**
 * @name scSearchProgressService
 * @kind service
 *
 * @description
 * Service to fetch progress data (specifically percent_to_goal) from Search application
 * to ensure consistency across all progress bars regardless of navigation method.
 * Always fetches fresh data to ensure real-time updates when donations come in.
 */

scSearchProgressService.$inject = ["$q", "classyQL"];
function scSearchProgressService($q, classyQL) {
  // Circuit breaker to prevent infinite loops
  var callTracker = {};
  var MAX_CALLS_PER_ENTITY = 3;
  var RESET_INTERVAL = 5000; // 5 seconds
  /**
   * Fetches campaign progress data from Search
   * @param {number} campaignId - Campaign ID
   * @returns {Promise} Promise resolving to campaign progress data
   */
  this.getCampaignProgress = function (campaignId) {
    var query = '\n      query {\n        campaignSearchPublic(\n          filter: { id: { eq: ' + campaignId + ' } },\n          count: 1\n        ) {\n          results {\n            id\n            percent_to_goal\n            total_raised\n            goal_raw\n            currency_code\n          }\n        }\n      }\n    ';

    return classyQL.query({ query: query }).then(function (response) {
      var results = response.data.data.campaignSearchPublic.results;
      if (results && results.length > 0) {
        return {
          percent_to_goal: results[0].percent_to_goal,
          total_raised: results[0].total_raised,
          goal_raw: results[0].goal_raw,
          currency_code: results[0].currency_code
        };
      }

      return null;
    }).catch(function () {
      return (
        // If Search fails, return null so we can fall back to APIv2
        null
      );
    });
  };

  /**
   * Fetches individual/team progress data from Search
   * @param {string} type - 'page' or 'team'
   * @param {number} entityId - Entity ID
   * @returns {Promise} Promise resolving to entity progress data
   */
  this.getEntityProgress = function (type, entityId) {
    // Safety check to prevent infinite loops
    if (!entityId || entityId === 'undefined' || entityId === 'null') {
      return $q.resolve(null);
    }

    // Circuit breaker: Track calls per entity to prevent infinite loops
    var trackingKey = type + '-' + entityId;
    var now = Date.now();

    if (!callTracker[trackingKey]) {
      callTracker[trackingKey] = { count: 0, firstCall: now };
    }

    callTracker[trackingKey].count += 1;

    // Reset counter if enough time has passed
    if (now - callTracker[trackingKey].firstCall > RESET_INTERVAL) {
      callTracker[trackingKey] = { count: 1, firstCall: now };
    }

    // If too many calls in short time, return null to break the loop
    if (callTracker[trackingKey].count > MAX_CALLS_PER_ENTITY) {
      // eslint-disable-next-line no-console
      console.error('Circuit breaker triggered: Too many calls to getEntityProgress for', trackingKey);
      return $q.resolve(null);
    }

    var queryType = type === 'page' ? 'fundraisingPageSearchPublic' : 'fundraisingTeamSearchPublic';

    // Different fields available for pages vs teams in public search
    var pageFields = '\n      id\n      percent_to_goal\n      total_raised\n      goal_raw\n      currency_code\n      normalized_currency_code\n      progress_bar_amount\n    ';

    var teamFields = '\n      id\n      percent_to_goal\n      total_raised\n      goal_raw\n      currency_code\n      progress_bar_amount\n    ';

    var fields = type === 'page' ? pageFields : teamFields;

    var query = '\n      query {\n        ' + queryType + '(\n          filter: { id: { eq: ' + entityId + ' } },\n          count: 1\n        ) {\n          results {\n            ' + fields + '\n          }\n        }\n      }\n    ';

    return classyQL.query({ query: query }).then(function (response) {
      var results = response.data.data[queryType].results;
      if (results && results.length > 0) {
        var result = results[0];
        return {
          percent_to_goal: result.percent_to_goal,
          total_raised: result.total_raised,
          goal_raw: result.goal_raw,
          currency_code: result.currency_code,
          // Only include normalized_currency_code if it exists (pages have it, teams don't)
          normalized_currency_code: result.normalized_currency_code || result.currency_code,
          progress_bar_amount: result.progress_bar_amount
        };
      }

      return null;
    }).catch(function () {
      return (
        // If Search fails, return null so we can fall back to APIv2
        null
      );
    });
  };

  /**
   * Fetches multiple entities progress data from Search in a single query
   * @param {string} type - 'page' or 'team'
   * @param {Array<number>} entityIds - Array of entity IDs
   * @returns {Promise} Promise resolving to map of entity progress data
   */
  this.getMultipleEntityProgress = function (type, entityIds) {
    if (!entityIds || entityIds.length === 0) {
      return $q.resolve({});
    }

    var queryType = type === 'page' ? 'fundraisingPageSearchPublic' : 'fundraisingTeamSearchPublic';

    // Different fields available for pages vs teams in public search
    var pageFields = '\n      id\n      percent_to_goal\n      total_raised\n      goal_raw\n      currency_code\n      normalized_currency_code\n      progress_bar_amount\n    ';

    var teamFields = '\n      id\n      percent_to_goal\n      total_raised\n      goal_raw\n      currency_code\n      progress_bar_amount\n    ';

    var fields = type === 'page' ? pageFields : teamFields;

    var query = '\n      query {\n        ' + queryType + '(\n          filter: { id: { in: [' + entityIds.join(', ') + '] } },\n          count: ' + entityIds.length + '\n        ) {\n          results {\n            ' + fields + '\n          }\n        }\n      }\n    ';

    return classyQL.query({ query: query }).then(function (response) {
      var results = response.data.data[queryType].results;
      var progressMap = {};

      if (results) {
        results.forEach(function (result) {
          var data = {
            percent_to_goal: result.percent_to_goal,
            total_raised: result.total_raised,
            goal_raw: result.goal_raw,
            currency_code: result.currency_code,
            // Only include normalized_currency_code if it exists (pages have it, teams don't)
            normalized_currency_code: result.normalized_currency_code || result.currency_code,
            progress_bar_amount: result.progress_bar_amount
          };

          progressMap[result.id] = data;
        });
      }

      return progressMap;
    }).catch(function () {
      return (
        // If Search fails, return empty object
        {}
      );
    });
  };
}

angular.module('classy').service('scSearchProgressService', scSearchProgressService);
})();